import { useState, useCallback, useEffect, useRef } from 'react';
import type { DriveItem, DriveNotice, DriveViewMode, BreadcrumbItem, CreateFolderData, CreateUrlData, DriveFolderNode, RenameItemData } from '../types';
import { driveApi, type DriveRow } from '../lib/drive-api';
import { sortDriveItems, toDriveItem, downloadFile } from '../lib/drive-items';

const ROOT: BreadcrumbItem[] = [{ id: 'root', name: 'Drive', folderId: null }];
const VIEW_KEY = 'krewup.drive.view';
const NOTICE_MS = 5000;

const readView = (): DriveViewMode => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
};

const messageOf = (error: unknown, fallback: string) => (error instanceof Error && error.message ? error.message : fallback);

/**
 * The state of the Drive screen for ONE project: the open folder (items + breadcrumb from a single API read), the selection and
 * the view. Every write goes through the API and then patches the list in place -- no folder reload per action.
 */
export function useDrive(projectId: string, companyId: string) {
  const [items, setItems] = useState<DriveItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>(ROOT);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewModeState] = useState<DriveViewMode>(readView);
  const [notice, setNotice] = useState<DriveNotice | null>(null);
  const requestRef = useRef(0);
  const noticeTimer = useRef<ReturnType<typeof setTimeout>>();
  const enabled = Boolean(projectId && projectId !== 'all' && companyId);

  const notify = useCallback((next: DriveNotice | null) => {
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    setNotice(next);
    if (next) noticeTimer.current = setTimeout(() => setNotice(null), NOTICE_MS);
  }, []);
  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); }, []);

  const setViewMode = useCallback((mode: DriveViewMode) => {
    setViewModeState(mode);
    try {
      localStorage.setItem(VIEW_KEY, mode);
    } catch {
      /* the choice just is not remembered */
    }
  }, []);

  /** Opens a folder. An answer that arrives after a newer navigation is discarded. */
  const navigateToFolder = useCallback(async (folderId: string | null) => {
    if (!enabled) return;
    const request = ++requestRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const data = await driveApi.listFolder(projectId, folderId);
      if (request !== requestRef.current) return;
      setItems(data.items.map(toDriveItem));
      setBreadcrumbs(data.breadcrumbs);
      setCurrentFolderId(folderId);
      setSelectedItems([]);
    } catch (e) {
      if (request !== requestRef.current) return;
      setError(messageOf(e, 'Failed to load this folder'));
    } finally {
      if (request === requestRef.current) setIsLoading(false);
    }
  }, [enabled, projectId]);

  // A different project always starts at its root.
  useEffect(() => {
    setItems([]);
    setBreadcrumbs(ROOT);
    setCurrentFolderId(null);
    setSelectedItems([]);
    if (enabled) navigateToFolder(null);
  }, [enabled, projectId, navigateToFolder]);

  const addItem = useCallback((row: DriveRow) => {
    setItems((prev) => sortDriveItems([...prev.filter((i) => i.id !== row.id), toDriveItem(row)]));
  }, []);

  const savedAsNotice = (asked: string, row: DriveRow): DriveNotice | null =>
    asked && row.name !== asked ? { title: `Saved as "${row.name}"`, description: `"${asked}" already exists in this folder.` } : null;

  const createFolder = useCallback(async (data: Pick<CreateFolderData, 'name'>) => {
    try {
      const row = await driveApi.createFolder({ ...data, parent_id: currentFolderId, project_id: projectId });
      addItem(row);
      notify(savedAsNotice(data.name, row));
      return row;
    } catch (e) {
      notify({ title: 'Could not create the folder', description: messageOf(e, 'Please try again.'), variant: 'destructive' });
      throw e;
    }
  }, [currentFolderId, projectId, addItem, notify]);

  const createUrl = useCallback(async (data: Pick<CreateUrlData, 'name' | 'url'>) => {
    try {
      const row = await driveApi.createUrl({ ...data, parent_id: currentFolderId, project_id: projectId });
      addItem(row);
      notify(savedAsNotice(data.name, row));
      return row;
    } catch (e) {
      notify({ title: 'Could not add the link', description: messageOf(e, 'Please try again.'), variant: 'destructive' });
      throw e;
    }
  }, [currentFolderId, projectId, addItem, notify]);

  const renameItem = useCallback(async (data: RenameItemData) => {
    try {
      const row = await driveApi.update(data.id, { name: data.newName, ...(data.newUrl !== undefined && { url: data.newUrl }) });
      addItem(row);
      notify(savedAsNotice(data.newName, row));
    } catch (e) {
      notify({ title: 'Could not save the changes', description: messageOf(e, 'Please try again.'), variant: 'destructive' });
      throw e;
    }
  }, [addItem, notify]);

  /** Uploads one after another into the open folder; a failed file never stops the next ones. */
  const uploadFiles = useCallback(async (files: File[]) => {
    const failures: string[] = [];
    const renamed: string[] = [];
    for (const file of files) {
      try {
        const row = await driveApi.uploadFile(file, { projectId, parentId: currentFolderId });
        addItem(row);
        if (row.name !== file.name) renamed.push(row.name);
      } catch (e) {
        failures.push(`${file.name}: ${messageOf(e, 'upload failed')}`);
      }
    }
    if (failures.length) {
      notify({ title: `${failures.length} file${failures.length === 1 ? '' : 's'} could not be uploaded`, description: failures.slice(0, 3).join('\n'), variant: 'destructive' });
    } else if (renamed.length) {
      notify({ title: renamed.length === 1 ? `Saved as "${renamed[0]}"` : `${renamed.length} files were renamed`, description: 'A file with that name already exists in this folder.' });
    }
    return { uploaded: files.length - failures.length, failed: failures.length };
  }, [projectId, currentFolderId, addItem, notify]);

  const deleteItems = useCallback(async (ids: string[]) => {
    try {
      const { deleted } = await driveApi.remove(ids);
      const gone = new Set(ids);
      setItems((prev) => prev.filter((i) => !gone.has(i.id)));
      setSelectedItems((prev) => prev.filter((id) => !gone.has(id)));
      notify({ title: deleted === 1 ? 'Item deleted' : `${deleted} items deleted` });
    } catch (e) {
      notify({ title: 'Could not delete', description: messageOf(e, 'Please try again.'), variant: 'destructive' });
      await navigateToFolder(currentFolderId); // some items may already be gone: show the truth
      throw e;
    }
  }, [notify, navigateToFolder, currentFolderId]);

  /** Moves items to a folder (null = the Drive root); the moved items leave the list of the open folder. */
  const moveItems = useCallback(async (ids: string[], parentId: string | null) => {
    try {
      const { moved } = await driveApi.move(ids, parentId);
      if (parentId !== currentFolderId) {
        const gone = new Set(ids);
        setItems((prev) => prev.filter((i) => !gone.has(i.id)));
        setSelectedItems((prev) => prev.filter((id) => !gone.has(id)));
      }
      const renamed = moved.filter((row) => items.some((i) => i.id === row.id && i.name !== row.name));
      notify({ title: moved.length === 1 ? 'Item moved' : `${moved.length} items moved`, description: renamed.length ? 'Items whose name was taken were renamed.' : undefined });
    } catch (e) {
      notify({ title: 'Could not move', description: messageOf(e, 'Please try again.'), variant: 'destructive' });
      throw e;
    }
  }, [currentFolderId, items, notify]);

  const loadFolders = useCallback((): Promise<DriveFolderNode[]> => driveApi.listFolders(projectId), [projectId]);

  const downloadItems = useCallback(async (list: DriveItem[]) => {
    try {
      for (const item of list.filter((i) => i.type === 'file')) await downloadFile(item);
    } catch (e) {
      notify({ title: 'Could not download', description: messageOf(e, 'Please try again.'), variant: 'destructive' });
    }
  }, [notify]);

  const selectItem = useCallback((itemId: string, isSelected: boolean) => {
    setSelectedItems((prev) => (isSelected ? (prev.includes(itemId) ? prev : [...prev, itemId]) : prev.filter((id) => id !== itemId)));
  }, []);

  /** Select all / none of the items shown in the open folder (never anything outside it). */
  const selectAllItems = useCallback((isSelected: boolean) => {
    setSelectedItems(isSelected ? items.map((i) => i.id) : []);
  }, [items]);

  const clearSelection = useCallback(() => setSelectedItems([]), []);

  return {
    items, currentFolderId, selectedItems, isLoading, error, breadcrumbs, viewMode, notice,
    setViewMode, notify, navigateToFolder, createFolder, createUrl, renameItem, uploadFiles, deleteItems, moveItems,
    loadFolders, downloadItems, selectItem, selectAllItems, clearSelection,
  };
}
